/* WeatherCard — replaces the legacy AlertCard. Shows live time, Iraqi-month
   date, current location and weather data fetched from Open-Meteo (free, no key).
   Reverse-geocoding via Nominatim (also free). Falls back to Baghdad if
   geolocation is denied or unavailable. */
import { useEffect, useRef, useState } from "react";
import { MapPin } from "@phosphor-icons/react";

const CARD =
  "bg-white/60 backdrop-blur-md rounded-[28px] border border-white/60 shadow-[0_2px_18px_rgba(10,25,47,0.08)]";

const IRAQI_MONTHS = [
  "كانون الثاني", "شباط", "آذار", "نيسان", "أيار", "حزيران",
  "تموز", "آب", "أيلول", "تشرين الأول", "تشرين الثاني", "كانون الأول",
];
const AR_DAYS = ["الأحد", "الإثنين", "الثلاثاء", "الأربعاء", "الخميس", "الجمعة", "السبت"];

// WMO weather code → emoji (Open-Meteo). https://open-meteo.com/en/docs
function codeToEmoji(c) {
  if (c === 0) return "☀️";
  if (c <= 3) return "⛅";
  if (c <= 48) return "🌫️";
  if (c <= 57) return "🌦️";
  if (c <= 65) return "🌧️";
  if (c <= 77) return "❄️";
  if (c <= 82) return "🌦️";
  if (c <= 86) return "🌨️";
  if (c >= 95) return "⛈️";
  return "🌤️";
}

function pad2(n) { return String(n).padStart(2, "0"); }

function formatDate(d) {
  return `${AR_DAYS[d.getDay()]}، ${d.getDate()} ${IRAQI_MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

function formatHijri(d) {
  try {
    return new Intl.DateTimeFormat("ar-u-ca-islamic-umalqura-nu-latn", {
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(d);
  } catch {
    return null;
  }
}

const DEFAULT_LOC = { lat: 33.3152, lon: 44.3661, city: "بغداد" };

async function fetchWeather(lat, lon) {
  const url =
    `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}` +
    "&current=temperature_2m,relative_humidity_2m,wind_speed_10m,weather_code" +
    "&timezone=auto";
  const res = await fetch(url);
  if (!res.ok) throw new Error("weather fetch failed");
  const j = await res.json();
  return j.current;
}

async function reverseGeocode(lat, lon) {
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lon}&format=json&accept-language=ar`,
      { headers: { Accept: "application/json" } },
    );
    if (!res.ok) return null;
    const j = await res.json();
    const a = j.address || {};
    return a.city || a.town || a.village || a.county || a.state || null;
  } catch {
    return null;
  }
}

function getPosition() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) return reject(new Error("no geolocation"));
    navigator.geolocation.getCurrentPosition(resolve, reject, {
      timeout: 6000,
      maximumAge: 10 * 60 * 1000,
    });
  });
}

/* Renders a string char-by-char with each glyph in a fixed-width inline-block.
   Guarantees zero layout shift even when the underlying font is proportional. */
function FixedString({ str, period }) {
  return (
    <span dir="ltr" className="inline-flex items-baseline whitespace-nowrap">
      {period && (
        <span
          className="inline-block text-center text-[15px] text-[#3D4654] font-normal me-1.5"
          style={{ width: "0.9em" }}
        >
          {period}
        </span>
      )}
      {str.split("").map((c, i) => (
        <span
          key={i}
          className="inline-block text-center"
          style={{ width: c === ":" ? "0.32em" : "0.6em" }}
        >
          {c}
        </span>
      ))}
    </span>
  );
}

/* Isolated clock: re-renders only itself every second so the rest of the
   card (date, city, weather) stays stable — no layout jitter. */
function LiveClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  const h = now.getHours();
  const period = h >= 12 ? "م" : "ص";
  const timeStr = `${pad2(h % 12 || 12)}:${pad2(now.getMinutes())}:${pad2(now.getSeconds())}`;
  return (
    <div
      data-testid="weather-time"
      className="font-tajawal font-bold text-[22px] leading-none text-[#0A192F]"
    >
      <FixedString str={timeStr} period={period} />
    </div>
  );
}

export default function WeatherCard() {
  const [today] = useState(() => new Date());
  const hijri = formatHijri(today);
  const [loc, setLoc] = useState({ city: "—" });
  const [w, setW] = useState({ temp: null, humidity: null, wind: null, icon: "🌤️" });
  const fetchedRef = useRef(false);

  // Fetch geolocation + weather once
  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    (async () => {
      let lat = DEFAULT_LOC.lat, lon = DEFAULT_LOC.lon, city = DEFAULT_LOC.city;
      try {
        const pos = await getPosition();
        lat = pos.coords.latitude;
        lon = pos.coords.longitude;
        const name = await reverseGeocode(lat, lon);
        if (name) city = name;
      } catch {
        // geo denied → keep Baghdad defaults
      }
      setLoc({ city });
      try {
        const cur = await fetchWeather(lat, lon);
        setW({
          temp: Math.round(cur.temperature_2m),
          humidity: Math.round(cur.relative_humidity_2m),
          wind: Math.round(cur.wind_speed_10m),
          icon: codeToEmoji(cur.weather_code),
        });
      } catch {
        // silent — clock + city still show
      }
    })();
  }, []);

  return (
    <section data-testid="weather-card" className={`${CARD} px-5 py-4 mb-3`}>
      <div className="flex justify-between items-center gap-4">
        {/* Right — time + dates */}
        <div className="flex flex-col gap-1.5 items-end text-right">
          <LiveClock />
          <div
            className="text-[14px] font-tajawal text-[#0A192F] leading-none"
            data-testid="weather-date-greg"
          >
            {formatDate(today)}
          </div>
          {hijri && (
            <div
              className="text-[15px] font-tajawal text-[#5A6470] leading-none"
              data-testid="weather-date-hijri"
            >
              {hijri}
            </div>
          )}
        </div>

        {/* Left — city · temperature · humidity-wind.
            Each row uses a w-6 icon slot so 📍 / ☀️ / 💧 stack on one vertical line. */}
        <div dir="ltr" className="flex flex-col gap-1.5 items-start">
          <div className="flex items-center gap-2" data-testid="weather-city">
            <div className="w-6 flex justify-center">
              <MapPin size={16} weight="fill" className="text-[#D4AF37]" />
            </div>
            <span className="text-[14px] font-tajawal text-[#0A192F] font-bold leading-none">
              {loc.city}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-6 flex justify-center">
              <span className="text-[22px] leading-none" aria-hidden="true">{w.icon}</span>
            </div>
            <div
              data-testid="weather-temp"
              className="font-tajawal font-bold text-[32px] leading-none text-[#0A192F] tabular-nums"
            >
              {w.temp !== null ? `${w.temp}°` : "—"}
              <span className="text-[15px] font-medium text-[#5A6470] ms-0.5">C</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-6 flex justify-center">
              <span className="text-[14px] leading-none" aria-hidden="true">💧</span>
            </div>
            <div className="flex items-center gap-2.5 text-[13px] font-tajawal text-[#5A6470] leading-none">
              {w.humidity !== null && (
                <span data-testid="weather-humidity" className="tabular-nums">
                  {w.humidity}%
                </span>
              )}
              {w.wind !== null && (
                <span data-testid="weather-wind" className="tabular-nums">
                  🌬️ {w.wind} km/h
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
